"use client";

import React, { useState } from "react";
import Link from "next/link";
import { Phone, Mail, MapPin } from "lucide-react";
import { FaHome, FaChevronRight } from "react-icons/fa";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { toast } from "react-toastify";
import config from "../config/config";

export default function ContactUs() {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);
    try {
      const apiUrl = `${config.NEXT_PUBLIC_API_URL}/api/public/message-to-party`;
      const res = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, message }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok)
        throw new Error(data?.message || `Request failed (${res.status})`);

      toast.success(data?.message || "Message sent successfully");
      setName("");
      setEmail("");
      setMessage("");
      setIsOpen(false);
    } catch (err) {
      toast.error(err.message || "Failed to send message");
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div className="min-h-screen relative">
      {/* Full Screen Map Background */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-fixed"
        style={{
          backgroundImage: "url(/images/map-ljpr-1.gif)",
        }}
      />

      {/* White Overlay for better readability */}
      <div className="absolute inset-0 bg-white/10" />

      {/* Content Overlay */}
      <div className="relative z-10">
        {/* Breadcrumb */}
        <div className="bg-white/90 backdrop-blur-sm py-3 px-4 md:px-8">
          <nav className="flex items-center gap-1 sm:gap-2 text-sm text-muted-foreground min-w-0">
            <Link
              href="/"
              className="flex items-center gap-1 hover:text-primary transition-colors flex-shrink-0"
            >
              <FaHome className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="text-xs sm:text-sm">Home</span>
            </Link>
            <FaChevronRight className="w-2 h-2 sm:w-3 sm:h-3 flex-shrink-0" />
            <span className="text-xs sm:text-sm font-medium text-foreground truncate min-w-0 flex-1">
              Contact Us
            </span>
          </nav>
        </div>

        {/* Header with Title and Button */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between px-4 md:px-8 py-6 bg-white/80 backdrop-blur-sm">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-0">
            Contact Us
          </h1>
          <div className="flex gap-2">
            <Button
              className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-md w-fit"
              onClick={() => setIsOpen(true)}
            >
              Write to party
            </Button>
            <Button
              variant="outline"
              className="
              bg-orange-500
              hover:bg-orange-600
              text-white
              px-6
              py-2
              rounded-md
              w-fit"
              onClick={() => window.open("mailto:support@ljpr.in", "_blank")}
            >
              Email directly
            </Button>
          </div>
        </div>

        {/* Contact Card */}
        <div className="px-4 md:px-8 py-8">
          <div className="max-w-md mx-auto md:mx-0 md:ml-auto md:mr-32">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              {/* Office Image */}
              <div className="h-48 bg-orange-100 relative">
                <img
                  src="/images/headquarter-1.png"
                  alt="LJPR Central Office"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextElementSibling.style.display = "flex";
                  }}
                  onLoad={(e) => {
                    if (e.target.nextElementSibling) {
                      e.target.nextElementSibling.style.display = "none";
                    }
                  }}
                />
                {/* Fallback for when image doesn't load */}
                <div className="absolute inset-0 bg-gradient-to-br from-orange-100 to-orange-200 hidden items-center justify-center">
                  <div className="text-center text-orange-600">
                    <div className="w-16 h-16 mx-auto mb-2 bg-orange-500 rounded-full flex items-center justify-center">
                      <MapPin className="w-8 h-8 text-white" />
                    </div>
                    <p className="font-semibold">LJP Central Office</p>
                  </div>
                </div>
              </div>

              {/* Contact Details */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Lok Janshakti Party (Ramvilas)
                </h3>
                <div className="bg-orange-500 text-white px-3 py-1 rounded text-xs font-bold inline-block mb-4">
                  CENTRAL OFFICE
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-gray-700">
                      <p>71, North Avenue,</p>
                      <p>New Delhi - 110001</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <div className="text-sm text-gray-700">
                      <span>+91-11-23094230</span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <div className="text-sm">
                      <a
                        href="mailto:support@ljpr.in"
                        className="text-orange-500 hover:text-orange-600 transition-colors"
                      >
                        support@ljpr.in
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dialog: Write to Party */}
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="w-full max-w-lg rounded-2xl">
            <DialogHeader>
              <DialogTitle>Write to Lok Janshakti Party (Ramvilas)</DialogTitle>
              <DialogDescription>
                Share your thoughts with us. We value your feedback.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <div className="space-y-2">
                <label className="text-sm font-medium">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your full name"
                  className="w-full px-3 py-2 border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-3 py-2 border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Message</label>
                <textarea
                  rows="5"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Write your message here"
                  className="w-full px-3 py-2 border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsOpen(false)}
                  className="min-w-[100px]"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-primary hover:bg-primary/90 min-w-[120px]"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
